import { cn } from '@shadcn/lib/utils';
import {
  Select as ShadcnSelect,
  SelectContent as ShadcnSelectContent,
  SelectItem as ShadcnSelectItem,
  SelectTrigger as ShadcnSelectTrigger,
  SelectValue as ShadcnSelectValue,
} from '@shadcn/ui/select';
import React from 'react';

import { Label } from '../label/label';

interface ISelectOption extends React.ComponentProps<typeof ShadcnSelectItem> {
  readonly className?: string;
  readonly value: string;
  readonly children?: React.ReactNode;
}

const SelectTriggerWrapper = ({
  selectId,
  label,
  labelId,
  placeholder,
  required,
  error,
  className,
}: {
  readonly selectId: string;
  readonly label?: string;
  readonly labelId: string;
  readonly placeholder?: string;
  readonly required?: boolean;
  readonly error?: boolean;
  readonly className?: string;
}) => {
  return (
    <ShadcnSelectTrigger
      aria-invalid={error}
      aria-label={label}
      aria-labelledby={labelId}
      aria-required={required}
      className={className}
      id={selectId}
    >
      <ShadcnSelectValue aria-label={placeholder ?? label} placeholder={placeholder} />
    </ShadcnSelectTrigger>
  );
};

const SelectOption = ({ value, ...props }: ISelectOption) => {
  return (
    <ShadcnSelectItem className={cn('cursor-pointer', props.className)} value={value} {...props}>
      {props.children}
    </ShadcnSelectItem>
  );
};

const SelectContentWrapper = ({
  labelId,
  children,
}: {
  readonly labelId: string;
  readonly children?: React.ReactNode;
}) => (
  <ShadcnSelectContent aria-labelledby={labelId} className="bg-neutral-0 border-neutral-400 text-typography">
    {children}
  </ShadcnSelectContent>
);

const getTriggerStyles = (className?: string, error?: boolean) =>
  cn(
    '!h-[29px] w-full cursor-pointer focus-visible:ring-indigo-700 bg-neutral-0 border-neutral-500 text-typography',
    error ? 'border-error' : '',
    className ?? ''
  );

interface ISelectProps extends React.ComponentProps<typeof ShadcnSelect> {
  readonly placeholder?: string;
  readonly id?: string;
  readonly value?: string;
  readonly defaultValue?: string;
  readonly onValueChange: (value: string) => void;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly className?: string;
  readonly label?: string;
  readonly required?: boolean;
  readonly children?: React.ReactNode;
}

function Select({
  defaultValue,
  id,
  placeholder,
  value,
  onValueChange,
  disabled,
  error,
  label,
  required,
  children,
  className,
  ...props
}: ISelectProps) {
  const generatedSelectId = React.useId();
  const selectId = id || generatedSelectId;
  const labelId = React.useId();
  const triggerStyles = getTriggerStyles(className, error);

  return (
    <div className="max-w-xs space-y-2">
      {label && (
        <Label htmlFor={selectId} id={labelId}>
          {label}
        </Label>
      )}
      <ShadcnSelect
        defaultValue={defaultValue}
        disabled={disabled}
        onValueChange={onValueChange}
        value={value}
        {...props}
      >
        <SelectTriggerWrapper
          className={triggerStyles}
          error={error}
          label={label}
          labelId={labelId}
          placeholder={placeholder}
          required={required}
          selectId={selectId}
        />
        <SelectContentWrapper labelId={labelId}>{children}</SelectContentWrapper>
      </ShadcnSelect>
    </div>
  );
}

export type { ISelectOption, ISelectProps };
export { Select, SelectOption };
